import React, {
    useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { groupWith } from 'ramda';

import t from '@services/locale';

import { Box } from '@components/atoms';
import {
    Button,
    CustomContainer,
    MotricityProgress,
} from '@components/molecules';
import {
    ActorsMotricityQuestion,
} from '@components/organisms';
import device from '@styles';
import { GetParsedColor } from '@styles/colors';

const splitByActor = groupWith((a, b) => {
    const actorA = a.short_name.split(',')[0];
    const actorB = b.short_name.split(',')[0];

    return actorA === actorB;
});

const ActorsMotricityForm = ({
    control, getValues, onSubmitEnabled, startingPoint,
}) => {
    const [hasPreviousValue, setHasPreviousValue] = useState(false);
    const [hasNextValue, setHasNextValue] = useState(true);
    const [section, setSection] = useState(startingPoint.sectionNumber);
    const [isDone, setIsDone] = useState(false);

    const currentActor = section - 1;
    const answers = getValues('answers');
    const chunks = useMemo(() => splitByActor(answers), [answers]);
    const sectionTitleRef = useRef(null);
    const isFirstLoadRed = useRef(true);

    const descriptionText = t('descrição pesquisa motricidade do ator');
    const lightBlue = GetParsedColor('lightBlue');

    const scrollToSectionTitle = () => {
        sectionTitleRef.current?.scrollIntoView({
            behavior: 'smooth',
        });
    };

    useEffect(() => {
        onSubmitEnabled(isDone);
    }, [isDone]);

    useEffect(() => {
        setHasPreviousValue(section > 1);
        const hasNext = chunks.length > section;
        setHasNextValue(hasNext);
        setIsDone((currentIsDone) => currentIsDone || !hasNext);
        if (!isFirstLoadRed.current)
            scrollToSectionTitle();
        isFirstLoadRed.current = false;
    }, [section]);

    const previous = () => {
        setSection((currentSection) => currentSection - 1);
    };

    const next = () => {
        setSection((currentSection) => currentSection + 1);
    };

    return (
        <>
            <CustomContainer>
                {descriptionText}
            </CustomContainer>
            <MotricityProgress
                currentQuestion={section}
                ref={sectionTitleRef}
                totalQuestions={chunks.length}
            />
            {
                chunks[currentActor].map((variable, index) => (
                    <ActorsMotricityQuestion
                        answers={answers}
                        control={control}
                        key={variable.short_name}
                        selectedCell={{
                            column: index,
                            context: variable.context,
                            row: currentActor,
                            shortName: variable.short_name,
                            statement: variable.statement,
                        }}
                    />
                ))
            }
            <Box
                display="flex"
                justifyContent="space-between"
            >
                <Button.Step
                    direction="left"
                    isDisabled={!hasPreviousValue}
                    style={{
                        borderColor: lightBlue,
                        width: device === 'web' ? '15rem' : '40vw',
                    }}
                    title="Ator anterior"
                    variant="outline"
                    onClick={previous}
                />

                <Button.Step
                    direction="right"
                    isDisabled={!hasNextValue}
                    style={{
                        borderColor: lightBlue,
                        width: device === 'web' ? '15rem' : '40vw',
                    }}
                    title="Ator seguinte"
                    variant="solid"
                    onClick={next}
                />
            </Box>
        </>
    );
};

ActorsMotricityForm.propTypes = {
    control: PropTypes.shape({
        register: PropTypes.func,
    }).isRequired,
    getValues: PropTypes.func.isRequired,
    onSubmitEnabled: PropTypes.func.isRequired,
    startingPoint: PropTypes.shape({
        questionNumber: PropTypes.number,
        sectionNumber: PropTypes.number,
    }).isRequired,
};

export default ActorsMotricityForm;
